import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { getCashAdvanceSummary } from "@/lib/financialSummary";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, HandCoins } from "@phosphor-icons/react";
import { KasbonDetailModal } from "@/components/DetailModals";
import { CashAdvanceStatusPill } from "@/components/PaymentStatusPill";
import { DeleteConfirmButton } from "@/components/DeleteConfirmButton";
import { CurrencyInput, DatePicker, MonthYearPicker } from "@/components/inputs";

export default function CashAdvancePage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const periodYears = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/cashadvances/me").then((res) => setItems(res.data));
  useEffect(() => { load(); }, []);

  const summary = getCashAdvanceSummary(items, month, year);

  const remove = async (id) => {
    await api.delete(`/cashadvances/${id}`);
    toast.success("Data dihapus");
    load();
  };

  return (
    <AppLayout title="Kasbon / Cash Advance">
      <div className="max-w-5xl space-y-6 animate-fade-up">
        {/* Part 1: summary */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1.5">
            <Label>Periode Bulan</Label>
            <MonthYearPicker month={month} year={year} onChange={(m, y) => { setMonth(m); setYear(y); }} years={periodYears} monthTestId="cashadvance-month-select" yearTestId="cashadvance-year-select" className="w-64" />
          </div>
          <AddDialog open={open} setOpen={setOpen} onDone={load} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card className="p-5">
            <p className="text-sm font-medium text-muted-foreground">Total Kasbon Keseluruhan</p>
            <p className="mt-1 font-heading text-2xl font-bold tabular-nums" data-testid="cashadvance-total">{formatRupiah(summary.total)}</p>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-medium text-muted-foreground">Potongan Periode Ini</p>
            <p className="mt-1 font-heading text-2xl font-bold tabular-nums text-destructive" data-testid="cashadvance-current-period-deduction">{formatRupiah(summary.currentPeriodDeduction)}</p>
          </Card>
        </div>

        {/* Part 2: table */}
        <Card className="overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">No</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead className="text-center">Cicilan</TableHead>
                <TableHead className="text-right">Per Bulan</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <HandCoins size={36} weight="duotone" className="mx-auto mb-2 opacity-40" />
                    Belum ada pengajuan kasbon.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((c, idx) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setDetail(c)} data-testid={`cashadvance-row-${idx}`}>
                    <TableCell>{c.no}</TableCell>
                    <TableCell className="max-w-xs truncate">{c.description}</TableCell>
                    <TableCell className="text-center">{c.installments}x</TableCell>
                    <TableCell className="text-right tabular-nums">{formatRupiah(c.monthly_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatRupiah(c.total_amount)}</TableCell>
                    <TableCell>
                      <CashAdvanceStatusPill status={c.status} />
                    </TableCell>
                    <TableCell>
                      <DeleteConfirmButton
                        title="Hapus cash advance?"
                        description="Pengajuan cash advance dan jadwal cicilannya akan dihapus permanen."
                        onConfirm={() => remove(c.id)}
                        triggerTestId={`delete-cashadvance-${idx}`}
                        confirmTestId={`confirm-delete-cashadvance-${idx}`}
                        stopPropagation
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
      <KasbonDetailModal item={detail} onClose={() => setDetail(null)} canManage={false} />
    </AppLayout>
  );
}

function AddDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({ description: "", installments: "1", total_amount: "", date: "" });
  const [saving, setSaving] = useState(false);

  const monthly = form.total_amount && form.installments
    ? Number(form.total_amount) / Number(form.installments)
    : 0;

  const submit = async () => {
    if (!form.total_amount) {
      toast.error("Lengkapi Jumlah Total");
      return;
    }
    setSaving(true);
    try {
      await api.post("/cashadvances", {
        description: form.description,
        installments: Number(form.installments) || 1,
        total_amount: Number(form.total_amount),
        date: form.date || null,
      });
      toast.success("Kasbon diajukan");
      setForm({ description: "", installments: "1", total_amount: "", date: "" });
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-cashadvance-button">
          <Plus size={18} weight="bold" /> Ajukan Kasbon
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Pengajuan Kasbon</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tanggal Pengajuan</Label>
            <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="cashadvance-date-picker" />
          </div>
          <div className="space-y-1.5">
            <Label>Keterangan</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contoh: Biaya darurat keluarga" data-testid="cashadvance-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Jumlah Total (Rp)</Label>
              <CurrencyInput value={form.total_amount} onChange={(num) => setForm({ ...form, total_amount: num })} data-testid="cashadvance-total-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Dicicil (kali)</Label>
              <Input type="number" min="1" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} placeholder="0" data-testid="cashadvance-installments-input" />
            </div>
          </div>
          <div className="rounded-lg bg-secondary p-4 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Potongan per bulan</span>
            <span className="font-heading font-bold tabular-nums" data-testid="cashadvance-monthly-preview">{formatRupiah(monthly)}</span>
          </div>
          <p className="text-xs text-muted-foreground">Nomor kasbon dibuat otomatis oleh sistem.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="submit-cashadvance-button">
            {saving ? "Menyimpan…" : "Ajukan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
